"use client";

import { ALGOLIA_HITS_PER_PAGE } from "@raumagarn/algolia";
import type { AlgoliaProductCard } from "@raumagarn/formatters";
import { clsx } from "@trydig/ui";
import { useIntersectionObserverAPI } from "@trydig/ui/hooks";
import type { Hit } from "instantsearch.js";
import {
	useInfiniteHits,
	useInstantSearch,
	useRefinementList,
	useSearchBox,
} from "react-instantsearch";
import { SearchTabs } from "~/features/algolia/components/AlgoliaSearchModal/components/SearchTabs";
import { useAlgoliaPopularProducts, useAlgoliaSearchModal } from "~/features/algolia/hooks";
import { ProductCard, ProductCardSkeleton } from "~/features/products/ProductCard";
import styles from "./SearchResults.module.css";

const Sentinel = ({ action, isLoading }: { action: () => void; isLoading?: boolean }) => {
	const obserrver = useIntersectionObserverAPI(([entry]) => {
		if (entry?.isIntersecting && !isLoading) {
			action();
		}
	});

	return (
		<span
			className={styles.sentinel}
			ref={(element) => {
				obserrver?.observe(element!);
				return () => {
					obserrver?.disconnect();
				};
			}}
		/>
	);
};

export const SearchResultsSkeleton = ({ size = ALGOLIA_HITS_PER_PAGE }: { size?: number }) => {
	return (
		<>
			{Array.from({ length: size })
				.fill(null)
				?.map((_, i) => (
					<ProductCardSkeleton key={i} />
				))}
		</>
	);
};

export const SearchResults = () => {
	const {
		items: products,
		showMore,
		isLastPage,
		results,
		sendEvent,
	} = useInfiniteHits<AlgoliaProductCard>();
	const { mode } = useAlgoliaSearchModal();
	const { query } = useSearchBox();
	const { status, error } = useInstantSearch({ catchError: true });

	const {
		items: facetItems,
		refine,
		canRefine,
	} = useRefinementList({
		attribute: "productType",
		limit: 10,
	});

	const hasQuery = !!query.trim();

	const { data: popularProducts, isLoading: isLoadingPopular } = useAlgoliaPopularProducts();

	const isLoading = status === "loading";
	const isFetchingNextPage = status === "stalled";

	if (error) return null;

	if (isLoading || (isLoadingPopular && !hasQuery)) {
		return (
			<div className={styles.products}>
				<SearchResultsSkeleton />
			</div>
		);
	}

	if (mode === "default") {
		const displayProducts = !hasQuery ? popularProducts : products;
		const noProducts = !displayProducts?.length;

		return (
			<>
				<p className={clsx(styles.headerText, hasQuery && styles.active)}>
					{hasQuery
						? noProducts
							? `Ingen produkter funnet for søk "${query}"`
							: "Produkter"
						: "Mest populært nå:"}
				</p>

				<div className={styles.products}>
					{displayProducts?.map((product, i) => (
						<ProductCard
							productCard={product}
							key={`${product.objectID}-${i}`}
							onClick={
								hasQuery
									? () => sendEvent("click", product as unknown as Hit, "Product Clicked")
									: undefined
							}
						/>
					))}
					{hasQuery && !isLastPage && <Sentinel action={showMore} isLoading={isFetchingNextPage} />}
					{hasQuery && isFetchingNextPage && <SearchResultsSkeleton />}
				</div>
			</>
		);
	}

	return (
		<>
			<p className={styles.searchResultsHeader}>Viser treff på &quot;{query}&quot;</p>
			<SearchTabs
				facetItems={facetItems}
				refine={refine}
				canRefine={canRefine}
				totalCount={results?.nbHits || 0}
			/>
			<div className={styles.products}>
				{products?.map((product, i) => (
					<ProductCard
						productCard={product}
						key={`${product.objectID}-${i}`}
						onClick={() => sendEvent("click", product as unknown as Hit, "Product Clicked")}
					/>
				))}
				{!isLastPage && <Sentinel action={showMore} isLoading={isFetchingNextPage} />}
				{isFetchingNextPage && <SearchResultsSkeleton />}
			</div>
		</>
	);
};
